import { uploadApi } from "@/shared/lib/upload-api";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Label,
  Loader,
  ScrollArea,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { useRouterBack } from "@/shared/hooks/useRouterBack";
import { ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  useBranchBillSettings,
  useUpdateBranchBillSettings,
} from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import type { BillLogoUploadFieldRef } from "../ui/BillLogoUploadField";
import { BillLogoUploadField } from "../ui/BillLogoUploadField";

export function BillSettingsPage() {
  const navigate = useNavigate();
  const goBack = useRouterBack();
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;

  const { data: billSettings, isLoading } = useBranchBillSettings(branchId);
  const updateMutation = useUpdateBranchBillSettings();
  const logoUploadRef = useRef<BillLogoUploadFieldRef>(null);

  const [logoUrl, setLogoUrl] = useState(billSettings?.logoUrl ?? "");
  const [brandName, setBrandName] = useState(billSettings?.brandName ?? "");
  const [phone, setPhone] = useState(billSettings?.phone ?? "");
  const [footerThankYouLao, setFooterThankYouLao] = useState(
    billSettings?.footerThankYouLao ?? "",
  );
  const [footerThankYouEnglish, setFooterThankYouEnglish] = useState(
    billSettings?.footerThankYouEnglish ?? "",
  );
  const [showTaxOnBill, setShowTaxOnBill] = useState(
    billSettings?.showTaxOnBill ?? true,
  );

  useEffect(() => {
    if (billSettings) {
      setLogoUrl(billSettings.logoUrl ?? "");
      setBrandName(billSettings.brandName ?? "");
      setPhone(billSettings.phone ?? "");
      setFooterThankYouLao(billSettings.footerThankYouLao ?? "");
      setFooterThankYouEnglish(billSettings.footerThankYouEnglish ?? "");
      setShowTaxOnBill(billSettings.showTaxOnBill ?? true);
    }
  }, [billSettings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchId) return;

    try {
      let finalLogoUrl: string | null = logoUrl.trim() || null;
      let oldKeyToDelete: string | null = null;

      const submitValue = await logoUploadRef.current?.getSubmitValue(branchId);
      if (submitValue) {
        finalLogoUrl = submitValue.logoUrl;
        oldKeyToDelete = submitValue.oldKeyToDelete ?? null;
      }

      if (oldKeyToDelete) {
        try {
          await uploadApi.deleteObject(oldKeyToDelete);
        } catch (delErr) {
          console.warn("Failed to delete old logo:", delErr);
        }
      }

      await updateMutation.mutateAsync({
        branchId,
        data: {
          logoUrl: finalLogoUrl,
          brandName: brandName.trim() || null,
          phone: phone.trim() || null,
          footerThankYouLao: footerThankYouLao.trim() || null,
          footerThankYouEnglish: footerThankYouEnglish.trim() || null,
          showTaxOnBill,
        },
      });

      logoUploadRef.current?.resetPending();
      setLogoUrl(finalLogoUrl ?? "");
      toast.success("ບັນທຶກຕັ້ງຄ່າບິນສຳເລັດແລ້ວ");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການບັນທຶກ",
      );
    }
  };

  if (!branchId) {
    return (
      <div className="container mx-auto max-w-2xl p-6">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon" />
            <EmptyTitle>ບໍ່ມີສາຂາເລືອກ</EmptyTitle>
            <EmptyDescription>
              ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນເຂົ້າເຖິງຕັ້ງຄ່າບິນ
            </EmptyDescription>
          </EmptyHeader>
          <Button onClick={() => navigate({ to: "/app/select-branch-device" })}>
            ເລືອກສາຂາ
          </Button>
        </Empty>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto flex max-w-2xl justify-center p-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl p-6">
      <ScrollArea className="max-h-[85vh] overflow-y-auto">
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-bold text-2xl">ຕັ້ງຄ່າບິນ</h1>
            <p className="text-muted-foreground text-sm">
              ຕັ້ງຄ່າ logo, ชื่อแบรนด์ ແລະ ເບີໂທສຳລັບສະແດງໃນບິນ
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>ຂໍ້ມູນທີ່ສະແດງໃນບິນ</CardTitle>
            <CardDescription>
              ຂໍ້ມູນເຫຼົ່ານີ້ຈະສະແດງໃນຫົວບິນຮັບເງິນ. ຖ້າບໍ່ກຳນົດ ລະບົບຈະໃຊ້ຊື່ສາຂາແທນ.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <BillLogoUploadField
                ref={logoUploadRef}
                value={logoUrl}
                branchId={branchId}
                disabled={updateMutation.isPending}
              />

              <div className="space-y-2">
                <Label htmlFor="brandName">ຊື່ແບຣນດ໌ / Brand Name</Label>
                <Input
                  id="brandName"
                  type="text"
                  placeholder={selection?.branchName ?? "ຊື່ຮ້ານ"}
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  ຖ້າບໍ່ກຳນົດ ຈະໃຊ້ຊື່ສາຂາ
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="showTaxOnBill"
                  checked={showTaxOnBill}
                  onCheckedChange={(checked) =>
                    setShowTaxOnBill(checked === true)
                  }
                />
                <Label
                  htmlFor="showTaxOnBill"
                  className="cursor-pointer font-normal"
                >
                  ເປີດໃຫ້ເຫັນພາສີໃນບິນ
                </Label>
              </div>
              <p className="text-muted-foreground text-xs">
                ເປີດເພື່ອສະແດງແຖວພາສີໃນບິນຮັບເງິນ
              </p>

              <div className="space-y-2">
                <Label htmlFor="phone">ເບີໂທ / Tel</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="020 1234 5678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="footerThankYouLao">ຂໍ້ຄວາມຂອບໃຈ (ລາວ)</Label>
                <Input
                  id="footerThankYouLao"
                  type="text"
                  placeholder="ຂອບໃຈທີ່ໃຊ້ບໍລິການ"
                  value={footerThankYouLao}
                  onChange={(e) => setFooterThankYouLao(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  ຂໍ້ຄວາມທ້າຍບິນເບິ່ງງານ (ພາສາລາວ)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="footerThankYouEnglish">ຂໍ້ຄວາມຂອບໃຈ (ອັງກິດ)</Label>
                <Input
                  id="footerThankYouEnglish"
                  type="text"
                  placeholder="Thank you for your visit"
                  value={footerThankYouEnglish}
                  onChange={(e) => setFooterThankYouEnglish(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  ຂໍ້ຄວາມທ້າຍບິນເບິ່ງງານ (ພາສາອັງກິດ)
                </p>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
                </Button>
                <Button type="button" variant="outline" onClick={goBack}>
                  ຍົກເລີກ
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </ScrollArea>
    </div>
  );
}
